import { useState, useRef, useEffect, useCallback, forwardRef } from 'react';
import * as React from 'react';

import clsx from 'clsx';
import { debounce } from 'lodash';
import { IoWarning } from 'react-icons/io5';
import { RiAddLine, RiSubtractLine } from 'react-icons/ri';

import { closestMultiple } from '@common/calculations';
import {
  Alert,
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Group,
  NumberInput,
  Stack,
  Text
} from '@ui';
import { convertToINR } from '@utils';

import classes from './CoverPicker.module.css';

// Simple validation icon component
const ValidationIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ marginRight: 4, marginTop: 2 }}
  >
    <circle cx="12" cy="12" r="10" fill="#ef4444" />
    <path d="M15 9l-6 6" stroke="white" strokeWidth="2" strokeLinecap="round" />
    <path d="m9 9 6 6" stroke="white" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

interface CoverChangeButtonProps {
  value: number;
  currentCover: number;
  min: number;
  max: number;
  disabled?: boolean;
  onClick: (modVal: number) => void;
}

const CoverChangeButton: React.FC<CoverChangeButtonProps> = React.memo(
  ({ value, currentCover, min, max, disabled, onClick }) => {
    const isDisabled =
      disabled ||
      (value < 0 && currentCover < Math.abs(value)) ||
      (value < 0 && currentCover <= min) ||
      (value > 0 && currentCover >= max);

    const IconComponent = value < 0 ? RiSubtractLine : RiAddLine;

    return (
      <Button
        variant="outline"
        color="secondary"
        fullWidth
        className={clsx({
          [classes.incButtonDisabled]: isDisabled
        })}
        disabled={isDisabled}
        onClick={(e) => {
          if (isDisabled) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }
          onClick(value);
        }}
        leftSection={
          value < 0 ? (
            <IconComponent
              className={clsx(classes.addIconRoot, {
                [classes.addIconDisabled]: isDisabled
              })}
            />
          ) : undefined
        }
        rightSection={
          value > 0 ? (
            <IconComponent
              className={clsx(classes.addIconRoot, {
                [classes.addIconDisabled]: isDisabled
              })}
            />
          ) : undefined
        }
      >
        {convertToINR(Math.abs(value), 2)}
      </Button>
    );
  }
);

interface CoverPickerProps {
  name?: string;
  title?: string;
  subTitle?: string;
  max?: number;
  min?: number;
  value?: number;
  disabled?: boolean;
  refresh?: boolean;
  onChange?: (value: number) => void;
  onLocalValChange?: (value: number) => void;
  customMaxError?: string;
}

interface CoverPickerError {
  message: string;
  type: 'default' | 'alert';
}

const defaultError: CoverPickerError = { message: '', type: 'default' };

const CoverPicker = forwardRef<HTMLDivElement, CoverPickerProps>(
  (
    {
      title,
      subTitle,
      name,
      max = 10_000_000,
      min = 50_00_000,
      value,
      disabled,
      refresh,
      onChange,
      onLocalValChange,
      customMaxError
    },
    ref
  ) => {
    const [localValue, setLocalValue] = useState<number>(value ? value : min);
    const isTextFocussed = useRef<boolean>(false);
    const [error, setError] = useState<CoverPickerError>(defaultError);

    useEffect(() => {
      if (refresh) {
        setLocalValue(value ?? 0);
      }
    }, [refresh]);

    useEffect(() => {
      setLocalValue(value ? value : min);
    }, [value]);

    useEffect(() => {
      if (!customMaxError) {
        setError(defaultError);
      }
    }, [customMaxError]);

    const debouncedOnChange = useRef(
      debounce((currValue: number) => {
        onChange?.(currValue);
      }, 400)
    );

    const handleFocus = () => {
      isTextFocussed.current = true;
    };

    const sanitizeValue = (input: any) => {
      if (!input || input === '') {
        return 0;
      } else if (typeof input === 'number') {
        return input;
      } else if (typeof input === 'string') {
        return parseInt(input.replace(/,/g, ''));
      } else {
        return input;
      }
    };

    const handleBlur = (input: any) => {
      let finalVal = min;
      const val = sanitizeValue(input);

      if (val <= min) {
        finalVal = min;
        if (min > 5000000) {
          setError({
            message: `You can’t pick a value less than ${convertToINR(
              min
            )} because the insurer don’t allow for it`,
            type: 'alert'
          });
        } else {
          setError({
            message: `You can’t pick a value less than 50 lakhs because most insurers don’t allow for it`,
            type: 'alert'
          });
        }
      } else if (val >= max) {
        finalVal = max;
        setError({
          message: `Maximum cover allowed for your profile is ${convertToINR(
            max,
            3,
            true
          )}`,
          type: 'alert'
        });
      } else {
        setError(defaultError);
        finalVal = val;
      }
      // find the closest multiple of 5L
      finalVal = closestMultiple(finalVal, 500000);
      if (value !== localValue) {
        setLocalValue(finalVal);
        debouncedOnChange.current(finalVal);
      }
      isTextFocussed.current = false;
    };

    const handleChange = (event: React.ChangeEvent<any> | null, value: number) => {
      let finalVal = value;
      if (typeof value === 'number') {
        finalVal = value;
      } else if (typeof value === 'string') {
        if (value === '') {
          finalVal = 0;
        }
      }
      setLocalValue(finalVal);
      onLocalValChange && onLocalValChange(finalVal);

      if (!isTextFocussed.current) {
        debouncedOnChange.current(finalVal);
      }
    };

    /**
     * function to modify the cover whenever the user makes any changes
     * propagateMaxError helps show the error related to max value change in parent
     */
    const modifyCover = useCallback(
      (modValue: number) => {
        let finalCover = localValue + modValue;
        finalCover = closestMultiple(finalCover, 500000);
        if (finalCover > max) {
          setError({
            message: !customMaxError
              ? `Maximum cover allowed for your profile is ${convertToINR(
                  max,
                  3,
                  true
                )}`
              : modValue >= 0
              ? customMaxError
              : '',
            type: !customMaxError || modValue < 0 ? 'default' : 'alert'
          });
          finalCover = max;
        } else if (finalCover < min) {
          if (min > 5000000) {
            setError({
              message: `Based on the provided details, the insurer allows a ${convertToINR(
                min
              )} minimum cover.`,
              type: 'default'
            });
          } else {
            setError({
              message: `You can’t pick a value less than 50 lakhs because most insurers don’t allow for it`,
              type: 'alert'
            });
          }
          finalCover = min;
        } else {
          setError({ message: '', type: 'default' });
        }
        setLocalValue(finalCover);
        debouncedOnChange.current(finalCover);
      },
      [min, max, localValue]
    );

    // whenever max value changes recheck cover is within min and max
    useEffect(() => {
      if (max && value && value > max) {
        modifyCover(0);
      }
    }, [max]);

    useEffect(() => {
      if (min && value && value < max) {
        modifyCover(0);
      }
    }, [min]);

    return (
      <div className={classes.root} ref={ref}>
        <Grid>
          <Grid.Col span={{ md: 8, sm: 8, xs: 8 }}>
            <Text size="md" fw={500}>
              {title}
            </Text>
            <Box mt={6}>
              <Text size="sm" c="dimmed">
                {subTitle}
              </Text>
            </Box>
          </Grid.Col>
          <Grid.Col span={{ md: 4, sm: 4, xs: 4 }} visibleFrom="md">
            <Box
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-end',
                width: '100%'
              }}
            >
              <Box className={classes.coverLabel}>
                <Text size="md" fw={600}>
                  {convertToINR(localValue, 3)}
                </Text>
              </Box>
            </Box>
          </Grid.Col>
        </Grid>
        {/* Desktop layout - show on medium screens and up */}

        <Group gap="sm" wrap="nowrap" mt={24} visibleFrom="md">
          <CoverChangeButton
            value={-5000000}
            currentCover={localValue}
            min={min}
            max={max}
            onClick={modifyCover}
            disabled={disabled}
          />
          <CoverChangeButton
            value={-1000000}
            currentCover={localValue}
            min={min}
            max={max}
            onClick={modifyCover}
            disabled={disabled}
          />

          <NumberInput
            name={`cover-picker-input-${name}`}
            onChange={(value) =>
              handleChange(null, parseFloat(value as string) || 0)
            }
            onBlur={(event) => handleBlur(event.target.value)}
            onFocus={handleFocus}
            value={localValue}
            placeholder=""
            disabled={disabled}
            styles={{ input: { textAlign: 'center', paddingLeft: 0 } }}
            leftSection="₹"
            size="xl"
            miw={190}
            useCurrencyFormatter
          />

          <CoverChangeButton
            value={1000000}
            currentCover={localValue}
            min={min}
            max={max}
            onClick={modifyCover}
            disabled={disabled}
          />
          <CoverChangeButton
            value={5000000}
            currentCover={localValue}
            min={min}
            max={max}
            onClick={modifyCover}
            disabled={disabled}
          />
        </Group>

        {/* Mobile layout - show on small screens and down */}
        <Box hiddenFrom="md">
          <Stack justify="center" align="center" mt={24}>
            <NumberInput
              name={`cover-picker-input-${name}`}
              onChange={(value) =>
                handleChange(null, parseFloat(value as string) || 0)
              }
              onBlur={(event) => handleBlur(event.target.value)}
              onFocus={handleFocus}
              value={localValue}
              placeholder=""
              disabled={disabled}
              classNames={{ section: classes.mobileInputSection }}
              leftSection="₹"
              size="xl"
              w="100%"
              useCurrencyFormatter
              rightSection={
                <Badge
                  color="secondary"
                  variant="light"
                  size="lg"
                  radius="xs"
                  w="100%"
                  mr="xs"
                  fw={500}
                >
                  {convertToINR(localValue, 3)}
                </Badge>
              }
            />
          </Stack>
          <Flex
            gap="sm"
            justify="space-between"
            mt={24}
            direction={{ base: 'column', sm: 'row' }}
          >
            <Group gap="sm" flex={1} wrap="nowrap" justify="space-between">
              <CoverChangeButton
                value={-5000000}
                currentCover={localValue}
                min={min}
                max={max}
                onClick={modifyCover}
                disabled={disabled}
              />
              <CoverChangeButton
                value={-1000000}
                currentCover={localValue}
                min={min}
                max={max}
                onClick={modifyCover}
                disabled={disabled}
              />
            </Group>
            <Group gap="sm" flex={1} wrap="nowrap" justify="space-between">
              <CoverChangeButton
                value={1000000}
                currentCover={localValue}
                min={min}
                max={max}
                onClick={modifyCover}
                disabled={disabled}
              />
              <CoverChangeButton
                value={5000000}
                currentCover={localValue}
                min={min}
                max={max}
                onClick={modifyCover}
                disabled={disabled}
              />
            </Group>
          </Flex>
        </Box>
        {error.message !== '' ? (
          error.type === 'default' ? (
            <Box className={classes.errorContainer}>
              <ValidationIcon />
              <Text size="sm" c="red">
                {error.message}
              </Text>
            </Box>
          ) : (
            <Alert
              variant="light"
              color="red"
              classNames={{
                root: classes.errorAlert,
                icon: classes.errorAlertIcon,
                message: classes.errorAlertMessage
              }}
              icon={<IoWarning size={16} />}
            >
              {error.message}
            </Alert>
          )
        ) : null}
      </div>
    );
  }
);

CoverPicker.displayName = 'CoverPicker';

export { CoverPicker };
export type { CoverPickerProps };
